import SpeakButton from "@/components/SpeakButton";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy } from "lucide-react";
import { useT } from "@/i18n";
import { MISSIONS, APP_TEXT, APP_TOOLS, canComplete } from "@/components/appbuilder/missions";
import useAppProject from "@/components/appbuilder/useAppProject";
import MissionMap from "@/components/appbuilder/MissionMap";
import PhonePreview from "@/components/appbuilder/PhonePreview";
import { IdeaMission, NameMission, LogoMission, FunMission } from "@/components/appbuilder/MissionsA";
import { ScreensMission, ButtonsMission, TestMission, FeedbackMission, ImproveMission, ShareMission } from "@/components/appbuilder/MissionsB";
import { AppHeader, RewardPop, FinalBadge } from "@/components/appbuilder/AppExtras";
import GameTools from "@/components/games/GameTools";

const PANELS = { idea: IdeaMission, name: NameMission, logo: LogoMission, screens: ScreensMission, buttons: ButtonsMission, fun: FunMission, test: TestMission, feedback: FeedbackMission, improve: ImproveMission, share: ShareMission };

export default function AppBuilder() {
  const { lang } = useT();
  const tx = APP_TEXT[lang];
  const { project, update, complete, share, isSignedIn, api, loadError, reload } = useAppProject();
  const [active, setActive] = useState(0);
  const [tested, setTested] = useState(false);
  const [reward, setReward] = useState(false);

  useEffect(() => {
    if (project) setActive(Math.min(project.missions.length, MISSIONS.length - 1));
  }, [project?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const wrap = (children) => (
    <section className="learn-bg rounded-[2rem] border-4 border-white shadow-xl p-4 sm:p-8 mt-8 space-y-8" data-testid="app-builder">{children}</section>
  );

  if (!isSignedIn) return wrap(<><AppHeader done={[]} /><div className="text-center"><Link to="/sign-in" className="glossy-btn inline-block bg-yellow-300 text-blue-900 font-fredoka font-bold text-xl px-8 py-3 rounded-full" data-testid="app-builder-signin">{tx.signIn}</Link></div></>);
  if (loadError) return wrap(<><AppHeader done={[]} /><div className="text-center"><button type="button" onClick={reload} className="glossy-btn bg-white text-blue-900 font-bold px-6 py-3 rounded-full" data-testid="app-builder-retry">{lang === "es" ? "Reintentar" : "Try again"}</button></div></>);
  if (!project) return wrap(<AppHeader done={[]} />);

  const mission = MISSIONS[active];
  const Panel = PANELS[mission.id];
  const isDone = project.missions.includes(mission.id);
  const ready = canComplete(project, mission.id, tested || project.missions.includes("test"));
  const allDone = project.missions.length === MISSIONS.length;

  const finish = async () => {
    const awarded = await complete(mission.id);
    if (awarded === null) return;
    if (awarded) { setReward(true); setTimeout(() => setReward(false), 1400); }
    if (active < MISSIONS.length - 1) setActive(active + 1);
  };

  return wrap(
    <>
      <AppHeader done={project.missions} />
      <MissionMap done={project.missions} active={active} onSelect={setActive} />
      {allDone && <FinalBadge />}
      <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-start">
        <div className="bg-white/95 rounded-[2rem] p-5 sm:p-6 shadow-lg" data-testid={`mission-panel-${mission.id}`}>
          <div className="flex items-start gap-3">
            <img src={`/img/${mission.guide}.webp`} alt="" className="w-14 sm:w-16 mascot-wiggle" />
            <div className="bg-sky-100 rounded-2xl rounded-tl-none px-4 py-2 flex-1">
              <p className="text-xs font-bold text-blue-700 uppercase">{lang === "es" ? "Misión" : "Mission"} {active + 1}</p>
              <p className="font-fredoka text-xl sm:text-2xl font-bold text-blue-950">{mission[lang][0]}</p>
              <p className="text-sm font-semibold text-blue-900">{mission[lang][1]}</p>
              <div className="mt-1.5"><SpeakButton key={`${mission.id}-${lang}`} compact mascot={mission.guide} text={`${mission[lang][0]}. ${mission[lang][1]}`} testId="ask-app-mission" /></div>
            </div>
          </div>
          <div className="mt-5">
            <Panel project={project} update={update} lang={lang} tx={tx} api={api} tested={tested || project.missions.includes("test")} share={share} />
          </div>
          <button type="button" onClick={finish} disabled={!ready} className={`glossy-btn mt-6 w-full sm:w-auto font-fredoka font-bold text-lg px-8 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50 ${isDone ? "bg-lime-300 text-green-900" : "bg-yellow-300 text-blue-900"}`} data-testid="mission-complete-btn">
            <Trophy className="w-5 h-5" /> {tx.done}
          </button>
        </div>
        <div className="lg:sticky lg:top-24">
          <PhonePreview project={project} onNavigate={() => setTested(true)} />
        </div>
      </div>
      <div className="pt-4">
        <h3 className="font-fredoka text-xl sm:text-2xl font-bold text-blue-950 mb-[-0.5rem]">{tx.tools}</h3>
        <GameTools tools={APP_TOOLS} hideTitle />
      </div>
      <RewardPop show={reward} />
    </>
  );
}
